import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import FormToggleField from "ds/components/Form/ToggleField";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";

import {
  GET_API_KEYS_MANAGEABLE_BY_NON_HUMANS,
  TOGGLE_API_KEYS_MANAGEABLE_BY_NON_HUMANS,
} from "./gql";
import styles from "./styles.module.css";

type ApiKeyManagementFromNonHumansToggleProps = {
  disabled?: boolean;
  tooltip?: string;
  plain?: boolean;
};

const ApiKeyManagementFromNonHumansToggle = ({
  disabled,
  tooltip,
  plain,
}: ApiKeyManagementFromNonHumansToggleProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const { data, loading: isLoading } = useQuery(GET_API_KEYS_MANAGEABLE_BY_NON_HUMANS, {});
  const { personalApiKeys } = useTypedFlags();
  const isEnabled = data?.apiKeysManageableByNonHumans || false;

  const [mutate, { loading: isSaving }] = useMutation(TOGGLE_API_KEYS_MANAGEABLE_BY_NON_HUMANS, {
    onError,
    onCompleted: (mutationData) => {
      const enabled = mutationData.accountToggleAPIKeyManagementFromNonHumans;

      reportSuccess({
        message: `API key creation from non-human sessions ${enabled ? "enabled" : "disabled"}`,
      });
    },
  });

  const toggle = useCallback(
    () =>
      mutate({
        refetchQueries: [{ query: GET_API_KEYS_MANAGEABLE_BY_NON_HUMANS }],
        awaitRefetchQueries: true,
      }),
    [mutate]
  );

  return (
    <Box className={plain ? undefined : styles.managementToggle} fullWidth>
      <FormToggleField
        variant="switch"
        title="API key creation from non-human sessions"
        description={
          personalApiKeys
            ? "Allow Organization API Keys to create, update or delete other API keys of the same type. Root Admin keys can update both types."
            : "Allow API keys to create, update, and delete other API keys."
        }
        checked={isEnabled}
        onChange={toggle}
        disabled={disabled || isLoading || isSaving}
        isInitialValueLoading={isLoading && !data}
        tooltip={tooltip ? () => tooltip : undefined}
      />
    </Box>
  );
};

export default ApiKeyManagementFromNonHumansToggle;
